import { API_URL } from "@/constants/api";
import {
  TransactionsResponse,
  ProjectsResponse,
  OrganizationsResponse,
  Project,
  Transaction,
  Purchase,
  Customer,
  ProjectDeveloper,
  ProjectMedia,
  PresignedUrlResponse,
  Country,
} from "@/types";
import { Retirement } from "@/types/retirement";
import { FeatureCollection } from "geojson";


const TRANSACTIONS_API_URL = `${API_URL}/v1/public/transactions`;
const PURCHASES_API_URL = `${API_URL}/v1/public/purchases`;
const PROJECTS_API_URL = `${API_URL}/v1/public/projects`;
const ORGANIZATIONS_API_URL = `${API_URL}/v1/public/organizations`;
const CUSTOMERS_API_URL = `${API_URL}/v1/public/customers`;
const BATCHES_API_URL = `${API_URL}/v1/public/batches`;
const PROJECT_DEVELOPERS_API_URL = `${API_URL}/v1/public/project-developers`;

export interface ProjectVideo {
  id: string;
  title: string;
  description?: string;
  url: string;
  thumbnailUrl?: string;
  projectId: string;
}

export const fetchTransactions = async (
  limit: number = 20,
  offset: number = 0,
  transactionId?: string,
  invoiceNumber?: string,
  projectId?: string,
): Promise<TransactionsResponse> => {
  let url = `${TRANSACTIONS_API_URL}?limit=${limit}&offset=${offset}`;

  if (transactionId) {
    url += `&transactionId=${transactionId}`;
  }

  if (invoiceNumber) {
    url += `&invoiceNumber=${invoiceNumber}`;
  }

  if (projectId) {
    url += `&projectId=${projectId}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const totalCount = parseInt(response.headers.get("x-total-count") || "0", 10);

  const data = await response.json();

  return {
    data,
    meta: {
      total: totalCount,
      limit,
      offset,
      pages: Math.ceil(totalCount / limit),
      page: Math.floor(offset / limit) + 1,
    },
  };
};

export const fetchTransactionByAccessId = async (
  accessId: string,
): Promise<Transaction> => {
  const url = `${TRANSACTIONS_API_URL}/${accessId}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const data = await response.json();

  return data;
};

export const fetchPurchaseById = async (
  purchaseId: string,
): Promise<Purchase> => {
  const url = `${PURCHASES_API_URL}/${purchaseId}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const data = await response.json();

  return data;
};

export const fetchProjects = async (
  limit: number = 20,
  offset: number = 0,
  projectDeveloperId?: string,
  countryCode?: string,
  bbox?: string,
): Promise<ProjectsResponse | FeatureCollection> => {
  let url = `${PROJECTS_API_URL}?limit=${limit}&offset=${offset}`;

  if (projectDeveloperId) {
    url += `&projectDeveloperId=${projectDeveloperId}`;
  }

  if (countryCode) {
    url += `&countryCode=${countryCode}`;
  }

  if (bbox) {
    url += `&bbox=${bbox}`;
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const data = await response.json();

  if (bbox) {
    return data as FeatureCollection;
  }

  const totalCount = parseInt(response.headers.get("x-total-count") || "0", 10);

  return {
    data,
    meta: {
      total: totalCount,
      limit,
      offset,
      pages: Math.ceil(totalCount / limit),
      page: Math.floor(offset / limit) + 1,
    },
  };
};

export const fetchProjectById = async (projectId: string): Promise<Project> => {
  const url = `${PROJECTS_API_URL}/${projectId}`;

  const cacheKey = `project-${projectId}`;
  const cachedData = sessionStorage.getItem(cacheKey);

  if (cachedData) {
    const { data, timestamp } = JSON.parse(cachedData);
    const cacheAge = Date.now() - timestamp;

    if (cacheAge < 5 * 60 * 1000) {
      return data;
    }
  }

  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/json",
      "Cache-Control": "max-age=300",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const data = await response.json();

  sessionStorage.setItem(
    cacheKey,
    JSON.stringify({
      data,
      timestamp: Date.now(),
    }),
  );

  return data;
};

export const getProjectDocumentUrl = (
  projectId: string,
  documentId: string,
): string => {
  return `${PROJECTS_API_URL}/${projectId}/documents/${documentId}`;
};

export const fetchProjectDocument = async (
  projectId: string,
  documentId: string,
): Promise<{ url: string }> => {
  const url = getProjectDocumentUrl(projectId, documentId);

  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const data = await response.json();

  return data;
};

export const fetchOrganizations = async (
  limit: number = 20,
  offset: number = 0,
): Promise<OrganizationsResponse> => {
  const url = `${ORGANIZATIONS_API_URL}?limit=${limit}&offset=${offset}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const totalCount = parseInt(response.headers.get("x-total-count") || "0", 10);

  const data = await response.json();

  return {
    data,
    meta: {
      total: totalCount,
      limit,
      offset,
      pages: Math.ceil(totalCount / limit),
      page: Math.floor(offset / limit) + 1,
    },
  };
};

export const fetchCustomerByAccessId = async (
  accessId: string,
): Promise<Customer> => {
  const url = `${CUSTOMERS_API_URL}/${accessId}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      accept: "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`API request failed with status ${response.status}`);
  }

  const data = await response.json();

  return data;
};

export const fetchProjectsByIds = async (
  projectIds: string[],
): Promise<Project[]> => {
  try {
    if (!projectIds || projectIds.length === 0) {
      return [];
    }

    const projectPromises = projectIds.map((id) => fetchProjectById(id));
    const projects = await Promise.all(projectPromises);

    const validProjects = projects.filter((p) => p) as Project[];

    return validProjects;
  } catch (error) {
    console.error("Error fetching projects by IDs:", error);
    return [];
  }
};

export const fetchBatchPlannedInvestments = async (batchId: string) => {
  try {
    const url = `${BATCHES_API_URL}/${batchId}/planned-investments`;

    const cacheKey = `batch-planned-investments-${batchId}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      const cacheAge = Date.now() - timestamp;

      if (cacheAge < 5 * 60 * 1000) {
        return data;
      }
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
        "Cache-Control": "max-age=300",
      },
    });

    if (!response.ok) {
      console.error(
        `API request failed for batch ${batchId} planned investments with status ${response.status}`,
      );
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();

    const processedData = Array.isArray(data)
      ? data.map((item) => {
          const plannedBudget =
            typeof item.plannedBudget === "number"
              ? item.plannedBudget
              : typeof item.plannedBudget === "string"
                ? parseFloat(item.plannedBudget)
                : typeof item.plannedAmount === "number"
                  ? item.plannedAmount
                  : typeof item.plannedAmount === "string"
                    ? parseFloat(item.plannedAmount)
                    : typeof item.amount === "number"
                      ? item.amount
                      : typeof item.amount === "string"
                        ? parseFloat(item.amount)
                        : 0;

          const category =
            item.category?.title ||
            item.category ||
            item.measureCategory?.title ||
            "other";

          return {
            ...item,
            plannedBudget: isNaN(plannedBudget) ? 0 : plannedBudget,
            category,
          };
        })
      : [];

    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        data: processedData,
        timestamp: Date.now(),
      }),
    );

    return processedData;
  } catch (error) {
    console.error("Error fetching batch planned investments:", error);
    throw error;
  }
};

export const fetchBatchMeasures = async (batchId: string) => {
  try {
    const url = `${BATCHES_API_URL}/${batchId}/measures`;

    const cacheKey = `batch-measures-${batchId}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      const cacheAge = Date.now() - timestamp;

      if (cacheAge < 5 * 60 * 1000) {
        return data;
      }
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
        "Cache-Control": "max-age=300",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();

    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      }),
    );

    if (Array.isArray(data)) {
      // Newest first.
      data.sort((a, b) => {
        const dateA = new Date(a.invoiceDate || a.date || 0).getTime();
        const dateB = new Date(b.invoiceDate || b.date || 0).getTime();
        return dateB - dateA;
      });
    }

    return data;
  } catch (error) {
    console.error("Error fetching batch measures:", error);
    throw error;
  }
};

export const fetchProjectMeasures = async (projectId: string) => {
  try {
    const url = `${PROJECTS_API_URL}/${projectId}/measures`;

    const cacheKey = `project-measures-${projectId}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      const cacheAge = Date.now() - timestamp;

      if (cacheAge < 5 * 60 * 1000) {
        return data;
      }
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
        "Cache-Control": "max-age=300",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();

    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      }),
    );

    if (Array.isArray(data)) {
      // Newest first.
      data.sort((a, b) => {
        const dateA = new Date(a.invoiceDate || a.date || 0).getTime();
        const dateB = new Date(b.invoiceDate || b.date || 0).getTime();
        return dateB - dateA;
      });
    }

    return data;
  } catch (error) {
    console.error("Error fetching project measures:", error);
    throw error;
  }
};

export const fetchProjectBatches = async (projectId: string) => {
  try {
    const url = `${PROJECTS_API_URL}/${projectId}/batches`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching project batches:", error);
    throw error;
  }
};

export const fetchProjectDevelopers = async (
  limit: number = 1000,
): Promise<ProjectDeveloper[]> => {
  try {
    const url = `${PROJECT_DEVELOPERS_API_URL}?limit=${limit}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error("Error fetching project developers:", error);
    return [];
  }
};

export const fetchProjectDeveloper = async (
  developerId: string,
): Promise<ProjectDeveloper | null> => {
  try {
    const url = `${PROJECT_DEVELOPERS_API_URL}`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const developers: ProjectDeveloper[] = await response.json();

    const developer = developers.find((dev) => dev.id === developerId) || null;

    return developer;
  } catch (error) {
    console.error("Error fetching project developer:", error);
    return null;
  }
};

export const fetchProjectMedia = async (
  projectId: string,
): Promise<ProjectMedia[]> => {
  try {
    const url = `${PROJECTS_API_URL}/${projectId}/media`;

    const cacheKey = `project-media-${projectId}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      const cacheAge = Date.now() - timestamp;

      if (cacheAge < 60 * 1000) {
        return data;
      }
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    });

    if (!response.ok) {
      console.error(
        `Failed to fetch media for project ${projectId}: ${response.status}`,
      );
      return [];
    }

    const data = await response.json();

    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      }),
    );

    return data;
  } catch (error) {
    console.error(`Error fetching media for project ${projectId}:`, error);
    return [];
  }
};

export const fetchMediaPresignedUrl = async (
  projectId: string,
  uri: string,
): Promise<PresignedUrlResponse | null> => {
  try {
    const encodedUri = encodeURIComponent(uri);
    const url = `${PROJECTS_API_URL}/${projectId}/media/${encodedUri}/presigned-url`;

    const cacheKey = `media-presigned-${projectId}-${uri}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      const cacheAge = Date.now() - timestamp;

      if (cacheAge < 5 * 60 * 1000) {
        return data;
      }
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    });

    if (!response.ok) {
      console.error(
        `Failed to fetch presigned URL for media ${uri}: ${response.status}`,
      );
      return null;
    }

    const data = await response.json();

    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      }),
    );

    return data;
  } catch (error) {
    console.error(`Error fetching presigned URL for media ${uri}:`, error);
    return null;
  }
};

export const fetchProjectVideos = async (
  projectId: string,
): Promise<ProjectVideo[]> => {
  try {
    const url = `${PROJECTS_API_URL}/${projectId}/videos`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    });

    if (!response.ok) {
      console.error(
        `Failed to fetch videos for project ${projectId}: ${response.status}`,
      );
      return [];
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching videos for project ${projectId}:`, error);
    return [];
  }
};

export const fetchCountries = async (): Promise<Country[]> => {
  try {
    const url = "https://api.fpm.tree.ly/v1/public/countries";

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching countries:", error);
    return [];
  }
};

export const fetchRetirements = async (
  transactionId: string,
): Promise<Retirement[]> => {
  try {
    const url = `${TRANSACTIONS_API_URL}/${transactionId}/retirements`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        accept: "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();

    return data.map((retirement: any) => ({
      id: retirement.id,
      amount: retirement.amount,
      createdAt: retirement.createdAt,
      updatedAt: retirement.updatedAt,
      description: retirement.description,
      externalConfirmationURL: retirement.externalConfirmationURL,
    }));
  } catch (error) {
    console.error("Error fetching retirements:", error);
    throw error;
  }
};
